const LOOPBACK_HOSTNAMES = new Set(['localhost', '127.0.0.1', '::1']);

/**
 * Resolves an API base URL for use from the current browser origin.
 *
 * When the configured URL points at a loopback host but the page itself was
 * loaded from a non-loopback origin (e.g. another device on the LAN), the
 * hostname is rewritten to match `window.location.hostname` so the browser
 * calls back to the same machine it loaded the app from. Scheme and port
 * from the configured URL are preserved.
 */
export function resolveApiOrigin(
  configuredUrl: string | undefined,
  pageHostname: string = window.location.hostname,
): string | undefined {
  if (!configuredUrl) {
    return configuredUrl;
  }

  let url: URL;
  try {
    url = new URL(configuredUrl);
  } catch {
    return configuredUrl;
  }

  const isConfiguredLoopback = LOOPBACK_HOSTNAMES.has(url.hostname);
  const isPageLoopback = LOOPBACK_HOSTNAMES.has(pageHostname);

  if (!isConfiguredLoopback || isPageLoopback) {
    return configuredUrl;
  }

  url.hostname = pageHostname;

  // `URL` unconditionally appends a trailing '/' when the input had no
  // path; preserve the original trailing-slash presence for callers that
  // concatenate paths onto this value.
  const hadTrailingSlash = configuredUrl.endsWith('/');
  const resolved = url.toString();
  return !hadTrailingSlash && url.pathname === '/' && resolved.endsWith('/')
    ? resolved.slice(0, -1)
    : resolved;
}

/**
 * The resolved API origin for the current browser session, derived from the
 * configured `VITE_API` value. Shared so callers concatenate paths against the
 * same origin the API clients use, without re-parsing env config.
 */
export const apiOrigin = resolveApiOrigin(import.meta.env.VITE_API);
